import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { ActivitySourceSchema } from '../activity/activities.schemas';
import {
  DealLossReasonSchema,
  DealPhaseDirectionSchema,
  DealPhaseSchema,
  DealStatusSchema,
  DealTypeSchema,
} from '../deal/deals.schemas';

export const DealCreatedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  type: DealTypeSchema,
  // The deal's initial phase and the `deal_phase_history` row that recorded it.
  // The activity timeline projects this as the first `deal.phase-changed` entry
  // (body `{ from: null, to: phase, direction: FORWARD }`), keyed on
  // `phaseHistoryId` so the live projection and the IMM-197 history backfill
  // converge on the same row.
  phase: DealPhaseSchema,
  phaseHistoryId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealCreatedPayload = z.infer<typeof DealCreatedPayloadSchema>;

// Emitted on every forward phase advance — the canonical record of a deal
// moving through the pipeline. Cross-cutting reactions (contract-received,
// won) subscribe to this single event and decide from `toPhase` / `status`
// whether they are interested, rather than the command branching per phase.
export const DEAL_PHASE_TRIGGERS = ['ONLINE_SIGNATURE'] as const;
export const DealPhaseTriggerSchema = z.enum(DEAL_PHASE_TRIGGERS);
export type DealPhaseTrigger = z.infer<typeof DealPhaseTriggerSchema>;

export const DealPhaseChangedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  // PK of the deal_phase_history row this change wrote — the activity store keys
  // its content_id on it so the live projection and the one-time backfill of
  // pre-existing history converge on the same row (IMM-197).
  phaseHistoryId: z.uuid(),
  fromPhase: DealPhaseSchema,
  toPhase: DealPhaseSchema,
  // FORWARD / BACKWARD — the activity timeline shows it ("advanced/reverted to…");
  // `status` stays on the event for the won/contract-received reactions but is
  // not part of the activity body.
  direction: DealPhaseDirectionSchema,
  status: DealStatusSchema,
  actor: ActorSchema,
  // Which surface performed the move (IMM-601.5): APP, or CONNECTED_CLIENT for
  // a workspace-MCP tool call. Travels on the payload so the projected timeline
  // entry can render the „Über einen verbundenen Client" marker. Optional with
  // a default so an event already sitting in the outbox across the deploy still
  // parses — absent means APP. Same vocabulary as the activity store's source.
  source: ActivitySourceSchema.default('APP'),
  // The audit row this move is to produce (IMM-894.1). Minted by the command
  // handler so the caller is told it in the same call, and used by the audit
  // subscriber as the row's primary key. OPTIONAL, so an event already sitting
  // in the outbox across the deploy still parses — absent means "generate one".
  auditEventId: z.uuid().optional(),
  // Set when an automatic move (the online signature) caused the change. Default keeps old outbox events parseable.
  trigger: DealPhaseTriggerSchema.nullable().default(null),
  occurredAt: z.iso.datetime(),
});

export type DealPhaseChangedPayload = z.infer<typeof DealPhaseChangedPayloadSchema>;

// Emitted when a deal advances into the CONTRACT_RECEIVED phase. The property
// module subscribes to flip the linked property Draft -> Active once its
// publish gate lands (IMM-5); until then the event is a recorded no-op.
export const DealContractReceivedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealContractReceivedPayload = z.infer<typeof DealContractReceivedPayloadSchema>;

// Emitted when a deal advances into the final ONLINE phase and is auto-won.
export const DealWonPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealWonPayload = z.infer<typeof DealWonPayloadSchema>;

// Emitted when an Eigentümer-Kontakt is added to / removed from a deal's
// roster. The IMM-6 owner link is ensured on add and left intact on remove;
// these record the deal-side membership change for the audit trail.
export const DealContactAddedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  // PK of the deal_contact_changes row — content_id for the activity store, so
  // live + backfill converge (IMM-197).
  contactChangeId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealContactAddedPayload = z.infer<typeof DealContactAddedPayloadSchema>;

export const DealContactRemovedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  // PK of the deal_contact_changes row — content_id for the activity store, so
  // live + backfill converge (IMM-197).
  contactChangeId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealContactRemovedPayload = z.infer<typeof DealContactRemovedPayloadSchema>;

// Emitted when a member appends to a deal's Notizen (Hindernisse / Nächste
// Schritte) timelines via the inline-edit endpoint. Records the edit for the
// audit trail; the free-text note itself is deliberately kept out of the
// payload so member-authored PII never lands in the audit log.
export const DealNotesUpdatedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealNotesUpdatedPayload = z.infer<typeof DealNotesUpdatedPayloadSchema>;

// Emitted when a deal is put on hold (`Zurückgestellt`) with a follow-up date.
// `deferredUntil` is the date the deal is due back; `actorId` is the member who
// deferred it. Re-targeting the date on an already-deferred deal emits it
// again, once per defer.
export const DealDeferredPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  deferredUntil: z.iso.date(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealDeferredPayload = z.infer<typeof DealDeferredPayloadSchema>;

// Emitted when a deferred deal is taken off hold (`Wieder aufnehmen`). `actor`
// is the resuming actor — a member for a manual resume, or the fixed system
// actor for the scheduled auto-resume. `statusChangeId` is the PK of the
// `deal_status_changes` row this resume wrote; the activity store keys its
// `content_id` on it (one entry per resume). `deferredBy` is the member who put
// the deal on hold (from the latest DEFERRED status row) — it scopes the
// auto-resume notice to the agent who deferred it (IMM-194); null when unknown.
export const DealResumedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  statusChangeId: z.uuid(),
  deferredBy: z.uuid().nullable(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealResumedPayload = z.infer<typeof DealResumedPayloadSchema>;

// Emitted at defer time and delivered by the outbox on the deal's follow-up date
// (`deliver_at` = `deferredUntil` at the workspace day's start). The subscriber
// re-runs the resume as the system actor — but only if the deal is still on hold
// for this exact date, so a re-targeted date or an early manual resume makes the
// (now stale) scheduled event a clean no-op. `actorId` is the member who deferred.
export const DealResumeScheduledPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  deferredUntil: z.iso.date(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealResumeScheduledPayload = z.infer<typeof DealResumeScheduledPayloadSchema>;

// Emitted when a deal is marked `Verloren` (`Als verloren markieren`). `lossReason`
// is the chosen Verlustgrund; the free-text Verlust-Notiz is deliberately kept out
// of the payload so member-authored PII never lands in the audit log. `lostFromPhase`
// is the phase the deal sat in at loss — the value a later reopen restores.
export const DealLostPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  lossReason: DealLossReasonSchema,
  lostFromPhase: DealPhaseSchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealLostPayload = z.infer<typeof DealLostPayloadSchema>;

// Emitted when a `Verloren` deal is reopened (`Wieder öffnen`). `restoredPhase` is
// the phase the deal returns to (the `lostFromPhase` captured at loss). The prior
// Verlustgrund / Verlust-Notiz stay in the status timeline but are cleared off the
// live deal.
export const DealReopenedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  restoredPhase: DealPhaseSchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealReopenedPayload = z.infer<typeof DealReopenedPayloadSchema>;

// Emitted when a deal is soft-deleted (`Löschen`). The deal is hidden from every
// list / get / filter response from this point; there is no hard delete in V1.
// `actor` is the Owner / Admin who deleted it.
export const DealDeletedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealDeletedPayload = z.infer<typeof DealDeletedPayloadSchema>;

// An immoOS operator restored a soft-deleted deal from the admin app (IMM-851.1).
// `actor` is the operating admin.
export const DealRestoredPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DealRestoredPayload = z.infer<typeof DealRestoredPayloadSchema>;

// An immoOS operator moved records of one Standort to another Standort of the same
// workspace from the admin app (IMM-1104). One event per move, listing every record
// that moved — the selection plus what followed it. `actor` is the operating admin.
export const OrganizationRecordsMovedPayloadSchema = z.object({
  workspaceId: z.uuid(),
  sourceOrganizationId: z.uuid(),
  targetOrganizationId: z.uuid(),
  dealIds: z.array(z.uuid()),
  propertyIds: z.array(z.uuid()),
  contactIds: z.array(z.uuid()),
  leadIds: z.array(z.uuid()),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type OrganizationRecordsMovedPayload = z.infer<typeof OrganizationRecordsMovedPayloadSchema>;

// What triggered a change to a deal's responsible-member set (IMM-402). The full
// enum is pinned now so later slices add emitters without a contract change; only
// `MANUAL_ASSIGNMENT` is emitted this slice (`CREATION` = creation seeding,
// `LEAD_QUALIFICATION` = qualification inheritance in IMM-402.2, `DEACTIVATION` =
// removal on member deactivation in IMM-402.3). English UPPER_CASE code
// identifiers per AGENTS.md — the German PRD tokens are web-app copy, not values.
export const DEAL_ASSIGNMENT_TRIGGERS = [
  'CREATION',
  'LEAD_QUALIFICATION',
  'MANUAL_ASSIGNMENT',
  'DEACTIVATION',
] as const;
export const DealAssignmentTriggerSchema = z.enum(DEAL_ASSIGNMENT_TRIGGERS);
export type DealAssignmentTrigger = z.infer<typeof DealAssignmentTriggerSchema>;

// Emitted when a deal's responsible-member set changes (IMM-402): a manual
// reassignment (`MANUAL_ASSIGNMENT`) this slice; creation seeding, qualification
// inheritance and deactivation removal in later slices. Carries the member id
// sets (ids only, ADR 0015) so the activity-timeline projection records the
// audit. `assignmentChangeId` is the idempotency key the projection keys on.
export const DealResponsibilityChangedPayloadSchema = z.object({
  dealId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  assignmentChangeId: z.uuid(),
  previousMemberIds: z.array(z.uuid()),
  newMemberIds: z.array(z.uuid()),
  trigger: DealAssignmentTriggerSchema,
  occurredAt: z.iso.datetime(),
});

export type DealResponsibilityChangedPayload = z.infer<typeof DealResponsibilityChangedPayloadSchema>;
